import { useState, useEffect } from "react";
import api from "../../services/api";
import { parseFechaLocal, parseFechaHoraLocal, formatPorcionesOCantidad } from "../../utils/pedidoDisplay";

const ESTADOS = [
  {
    key: "pendiente",
    label: "Pendiente",
    color: "text-amber-700",
    bg: "bg-amber-50",
    border: "border-amber-200",
    headerBg: "bg-amber-50",
    siguiente: "produccion",
    btnLabel: "Iniciar producción",
    btnColor: "bg-[#1A56DB] hover:bg-[#1E429F] text-white",
  },
  {
    key: "produccion",
    label: "En Producción",
    color: "text-blue-700",
    bg: "bg-blue-50",
    border: "border-blue-200",
    headerBg: "bg-blue-50",
    siguiente: "listo",
    btnLabel: "Marcar listo",
    btnColor: "bg-green-600 hover:bg-green-700 text-white",
  },
  {
    key: "listo",
    label: "Listo",
    color: "text-green-700",
    bg: "bg-green-50",
    border: "border-green-200",
    headerBg: "bg-green-50",
    siguiente: null,
    btnLabel: null,
    btnColor: null,
  },
  {
    key: "despachado",
    label: "Despachado",
    color: "text-gray-500",
    bg: "bg-[#F1F4F9]",
    border: "border-[#E5E9F0]",
    headerBg: "bg-[#F1F4F9]",
    siguiente: null,
    btnLabel: null,
    btnColor: null,
  },
];

export default function PanelProduccion() {
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState("hoy");

  useEffect(() => {
    cargarPedidos();
    const interval = setInterval(cargarPedidos, 15000);
    return () => clearInterval(interval);
  }, []);

  const cargarPedidos = async () => {
    try {
      const res = await api.get("/pedidos");
      setPedidos(res.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const cambiarEstado = async (id, nuevoEstado) => {
    try {
      await api.patch(`/pedidos/${id}/estado`, { estado: nuevoEstado });
      cargarPedidos();
    } catch (err) {
      console.error(err);
    }
  };

  const hoy = new Date();

  const pedidosFiltrados = pedidos.filter((p) => {
    const fecha = parseFechaLocal(p.fecha_entrega);
    if (!fecha) return false;

    if (periodo === "hoy") {
      return fecha.toDateString() === hoy.toDateString();
    }

    if (periodo === "manana") {
      const manana = new Date();
      manana.setDate(hoy.getDate() + 1);
      return fecha.toDateString() === manana.toDateString();
    }

    if (periodo === "semana") {
      const hace7Dias = new Date();
      hace7Dias.setDate(hoy.getDate() - 7);
      return fecha >= hace7Dias && fecha <= hoy;
    }

    return true;
  });

  const pedidosOrdenados = [...pedidosFiltrados].sort((a, b) => {
    const fechaA = parseFechaHoraLocal(a.fecha_entrega, a.hora_entrega);
    const fechaB = parseFechaHoraLocal(b.fecha_entrega, b.hora_entrega);
    return fechaA - fechaB;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F1F4F9] flex items-center justify-center">
        <div className="flex items-center gap-3 text-[#1A56DB]">
          <div className="w-5 h-5 border-2 border-[#1A56DB] border-t-transparent rounded-full animate-spin"></div>
          <span className="text-sm font-medium">Cargando pedidos...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F1F4F9] p-4">
      <div className="flex gap-2 mb-4 flex-wrap">
        {[
          { key: "hoy", label: "Hoy" },
          { key: "manana", label: "Mañana" },
          { key: "semana", label: "Semana" },
          { key: "todos", label: "Todos" },
        ].map((item) => (
          <button
            key={item.key}
            onClick={() => setPeriodo(item.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              periodo === item.key
                ? "bg-[#1A56DB] text-white"
                : "bg-white text-gray-600 border border-[#E5E9F0] hover:border-[#1A56DB] hover:text-[#1A56DB]"
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 h-[calc(100vh-145px)]">
        {ESTADOS.map((estado) => {
          const pedidosEstado = pedidosOrdenados.filter(
            (p) => p.estado === estado.key,
          );

          return (
            <div
              key={estado.key}
              className="bg-white border border-[#E5E9F0] rounded-xl overflow-hidden shadow-sm flex flex-col min-h-0"
            >
              <div
                className={`px-3 py-2.5 border-b border-[#E5E9F0] ${estado.headerBg} flex items-center justify-between`}
              >
                <span
                  className={`text-xs font-semibold uppercase tracking-wider ${estado.color}`}
                >
                  {estado.label}
                </span>
                <span
                  className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${estado.bg} ${estado.color} ${estado.border}`}
                >
                  {pedidosEstado.length}
                </span>
              </div>

              <div className="p-2 space-y-2 overflow-y-auto flex-1">
                {pedidosEstado.length === 0 && (
                  <div className="text-center text-gray-300 text-xs py-6">
                    Sin pedidos
                  </div>
                )}

                {pedidosEstado.map((pedido) => (
                  <div
                    key={pedido.id}
                    className={`${estado.bg} border ${estado.border} rounded-lg p-3`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-semibold text-[#1A56DB]">
                        {pedido.numero_pedido}
                      </span>
                      <span
                        className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                          pedido.local_origen === "sur"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {pedido.local_origen?.toUpperCase()}
                      </span>
                    </div>

                    <div className="text-sm font-semibold text-gray-900 mb-1">
                      {pedido.cliente_nombre}
                      {pedido.decoracion !== "ninguno" && (
                        <span className="ml-1 text-base">
                          {pedido.decoracion === "mujer" ? "👩" : "👨"}
                        </span>
                      )}
                    </div>

                    <div className="text-xs text-[#1A56DB] font-medium mb-1">
                      🎂 {pedido.producto}
                    </div>

                    <div className="text-xs text-gray-500 mb-1">
                      {formatPorcionesOCantidad(pedido)}
                      {pedido.es_personalizada && (
                        <span className="ml-1 text-amber-600 font-medium">
                          ★ Personalizada
                        </span>
                      )}
                    </div>

                    {Array.isArray(pedido.variedades) && pedido.variedades.length > 0 && (
                      <div className="text-xs bg-[#1A56DB] text-white rounded px-2 py-1 mb-1 font-medium">
                        Variedades: {pedido.variedades.join(', ')}
                      </div>
                    )}

                    {pedido.detalle_especial && (
                      <div className="text-xs bg-white border border-amber-200 rounded px-2 py-1 text-amber-700 mb-2">
                        📝 {pedido.detalle_especial}
                      </div>
                    )}

                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-700">
                        {pedido.hora_entrega?.slice(0, 5)} hrs
                      </span>
                      <span className="text-xs text-gray-400">
                        {parseFechaLocal(pedido.fecha_entrega)?.toLocaleDateString(
                          "es-CL",
                          { day: "numeric", month: "short" },
                        )}
                      </span>
                    </div>

                    {estado.siguiente && (
                      <button
                        onClick={() =>
                          cambiarEstado(pedido.id, estado.siguiente)
                        }
                        className={`w-full text-xs font-medium py-1.5 rounded-lg transition-all ${estado.btnColor}`}
                      >
                        {estado.btnLabel}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
